import React, { useState } from 'react';
import { Phone, Mail, Instagram, MapPin, Send, CheckCircle, Zap, Fuel } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { contactData, vehicleGallery } from '../../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 3000);
  };

  return (
    <>
      {/* Vehicle Gallery Section */}
      <section className="py-20 lg:py-32 bg-gray-900 relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#63E61E]/20 rounded-full mb-4">
              <span className="text-[#63E61E] font-bold text-sm tracking-wide">NUESTROS VEHÍCULOS</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
              Elige tu Vehículo Ideal
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Contamos con una flota diversa de vehículos eléctricos y a gasolina para que elijas el que mejor se adapte a tus necesidades.
            </p>
          </div>

          {/* Vehículos Eléctricos */}
          <div className="mb-12">
            <div className="flex items-center justify-center space-x-3 mb-8">
              <Zap className="w-8 h-8 text-[#63E61E]" />
              <h3 className="text-3xl font-bold text-white">Vehículos Eléctricos</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {vehicleGallery.electric.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src={image}
                    alt={`Vehículo Eléctrico ${index + 1}`}
                    className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#63E61E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 bg-[#63E61E] px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-900 font-bold text-sm">Eléctrico</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Vehículos a Gasolina */}
          <div>
            <div className="flex items-center justify-center space-x-3 mb-8">
              <Fuel className="w-8 h-8 text-gray-400" />
              <h3 className="text-3xl font-bold text-white">Vehículos a Gasolina</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {vehicleGallery.gasoline.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src={image}
                    alt={`Vehículo a Gasolina ${index + 1}`}
                    className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 bg-white px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-900 font-bold text-sm">Gasolina</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-[#63E61E]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-[#63E61E]/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-[#63E61E]/10 rounded-full mb-4">
              <span className="text-[#63E61E] font-bold text-sm tracking-wide">CONTÁCTANOS</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
              {contactData.title}
            </h2>
            <p className="text-lg text-gray-700">{contactData.description}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Información de Contacto</h3>
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#63E61E]/10 rounded-xl flex items-center justify-center group-hover:bg-[#63E61E] transition-colors duration-300">
                      <Phone className="w-6 h-6 text-[#63E61E] group-hover:text-gray-900 transition-colors duration-300" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Teléfono</h4>
                      <a href={`tel:${contactData.phone}`} className="text-gray-600 hover:text-[#63E61E] transition-colors">
                        {contactData.phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#63E61E]/10 rounded-xl flex items-center justify-center group-hover:bg-[#63E61E] transition-colors duration-300">
                      <Mail className="w-6 h-6 text-[#63E61E] group-hover:text-gray-900 transition-colors duration-300" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <a href={`mailto:${contactData.email}`} className="text-gray-600 hover:text-[#63E61E] transition-colors">
                        {contactData.email}
                      </a>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#63E61E]/10 rounded-xl flex items-center justify-center group-hover:bg-[#63E61E] transition-colors duration-300">
                      <Instagram className="w-6 h-6 text-[#63E61E] group-hover:text-gray-900 transition-colors duration-300" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Instagram</h4>
                      <a
                        href={`https://instagram.com/${contactData.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#63E61E] transition-colors"
                      >
                        @{contactData.instagram}
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#63E61E]/10 rounded-xl flex items-center justify-center group-hover:bg-[#63E61E] transition-colors duration-300">
                      <MapPin className="w-6 h-6 text-[#63E61E] group-hover:text-gray-900 transition-colors duration-300" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Ubicación</h4>
                      <p className="text-gray-600">{contactData.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="hidden lg:block relative rounded-2xl overflow-hidden shadow-2xl group">
                <img
                  src="https://images.unsplash.com/photo-1551650975-87deedd944c3"
                  alt="Contacto LUCARS"
                  className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center h-full space-y-4 py-12">
                  <div className="w-20 h-20 bg-[#63E61E]/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-[#63E61E]" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">¡Mensaje Enviado!</h3>
                  <p className="text-gray-600 text-center">
                    Gracias por contactarnos. Te responderemos pronto.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Nombre Completo
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre"
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-[#63E61E] focus:ring-[#63E61E]"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-[#63E61E] focus:ring-[#63E61E]"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Teléfono
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+52 999 000 0000"
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-[#63E61E] focus:ring-[#63E61E]"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                      Mensaje
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Cuéntanos cómo podemos ayudarte..."
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-[#63E61E] focus:ring-[#63E61E] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#63E61E] hover:bg-[#52c518] text-gray-900 font-bold py-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <span>Enviar Mensaje</span>
                    <Send className="w-5 h-5" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
